import { AdyenAmexIcon } from 'asset/adyen_amex_icon'
import { AdyenCardIcon } from 'asset/adyen_card_icon'
import { AdyenDiscoverIcon } from 'asset/adyen_discover_icon'
import { AdyenMasterCardIcon } from 'asset/adyen_mastercard_icon'
import { AdyenVisaIcon } from 'asset/adyen_visa_icon'
import { formatInTimeZone } from 'date-fns-tz'
import { ExternalStoredPaymentMethodAdminDto, ExternalStoredPaymentMethodClientDto } from 'service-api/src/types/api'
import { DEFAULT_TIMEZONE, getMonthShortName } from './dates'
import { i18WithParams as t } from './locales'

type CardBrandConfig = {
  displayName: string
  logo: JSX.Element
}

const CARD_BRAND_CONFIG: Record<string, CardBrandConfig> = {
  visa: {
    displayName: 'Visa',
    logo: <AdyenVisaIcon />,
  },
  amex: {
    displayName: 'Amex',
    logo: <AdyenAmexIcon />,
  },
  discover: {
    displayName: 'Discover',
    logo: <AdyenDiscoverIcon />,
  },
  mc: {
    displayName: 'Mastercard',
    logo: <AdyenMasterCardIcon />,
  },
  maestro: {
    displayName: 'Maestro',
    logo: <AdyenCardIcon />,
  },
  jcb: {
    displayName: 'JCB',
    logo: <AdyenCardIcon />,
  },
  cub: {
    displayName: 'Unionpay',
    logo: <AdyenCardIcon />,
  },
  diners: {
    displayName: 'Diners',
    logo: <AdyenCardIcon />,
  },
}

const DEFAULT_CARD_CONFIG: CardBrandConfig = {
  displayName: `[${t('customerProfile.cardsOnFile.otherCard')}]`,
  logo: <AdyenCardIcon />,
}

export const isCardExpired = (month?: string, year?: string, timezone?: string | null): boolean => {
  if (month && year) {
    const expiryYear = 2000 + parseInt(year, 10)
    const expiryMonth = parseInt(month, 10)
    const currentYear = parseInt(formatInTimeZone(new Date(), timezone || DEFAULT_TIMEZONE, 'yyyy'), 10)
    const currentMonth = parseInt(formatInTimeZone(new Date(), timezone || DEFAULT_TIMEZONE, 'MM'), 10)
    return expiryYear < currentYear || (expiryYear === currentYear && expiryMonth < currentMonth)
  } else {
    return true
  }
}

export const formatExpiryDate = (month?: string, year?: string): string => {
  if (month && year) {
    const formattedMonth = getMonthShortName(Number(month))
    const fullYear = 2000 + parseInt(year, 10)
    return `${formattedMonth} ${fullYear}`
  } else {
    return ''
  }
}

export const getAdyenCardDetails = (
  card: ExternalStoredPaymentMethodAdminDto | ExternalStoredPaymentMethodClientDto,
  timezone?: string | null
) => {
  const cardType = card.brand?.toLowerCase() || ''
  const matchedBrand = Object.entries(CARD_BRAND_CONFIG).find(([key]) => cardType.startsWith(key))
  const { displayName, logo } = matchedBrand ? matchedBrand[1] : DEFAULT_CARD_CONFIG
  return {
    displayName,
    logo,
    lastFourDigits: card.lastFour,
    expiry: formatExpiryDate(card.expiryMonth, card.expiryYear),
    isExpired: isCardExpired(card.expiryMonth, card.expiryYear, timezone),
  }
}

export const externalStoredPaymentMethodType = (
  paymentMethod?: ExternalStoredPaymentMethodAdminDto | ExternalStoredPaymentMethodClientDto,
) => {
  const { brand, lastFour } = paymentMethod || {}
  if (!brand && !lastFour) return null
  const baseBrand = CARD_BRAND_CONFIG[brand?.toLowerCase() || '']?.displayName || t('global.label.card')
  return lastFour ? t('global.label.cardEndingIn', { label: baseBrand, lastFour }) : baseBrand
}
